import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Input, Popover, Tooltip, theme } from 'antd'
import type { InputRef } from 'antd'
import { CheckOutlined, CloseOutlined, DownOutlined, LockOutlined, MinusOutlined, SearchOutlined } from '@ant-design/icons'

export interface FilterOption {
  value: string
  label: string
  /**
   * A colour this option already carries elsewhere in the product - a leave type's colour on the
   * wallchart grid, say. Rendered as a swatch before the label so the filter reads in the same
   * terms as the thing it filters. Decorative: the label always says the same thing, so the
   * swatch is `aria-hidden` and nothing depends on distinguishing the colours.
   */
  swatch?: string
  /**
   * Nesting depth for hierarchical options (departments with subdepartments): the row indents by
   * one step per level. Presentation only - the label stays the clean name, so the trigger text
   * and any summaries built from it carry no indentation.
   * @default 0
   */
  indentLevel?: number
  /**
   * A heading for the options nested under it (the ones after it with a deeper indentLevel), not
   * an option itself: it never enters the value. A multi-select panel draws it, like any option
   * with options nested under it, as a group that opens and closes, whose box ticks all of them.
   */
  group?: boolean
}

/** Matches the legend tags it echoes: a small rounded square, not a dot. */
export function OptionSwatch({ color }: { color: string }) {
  return (
    <span
      aria-hidden
      style={{
        width: 12,
        height: 12,
        borderRadius: 3,
        background: color,
        flex: 'none',
        // Pale leave-type colours otherwise dissolve into the panel.
        boxShadow: 'inset 0 0 0 1px rgba(0, 0, 0, 0.08)',
      }}
    />
  )
}

/** The end-of-row box a multi-select row shows: ticked, partly ticked (a group), or not. Decorative - the row carries aria-selected. */
export function OptionBox({ checked, mixed = false }: { checked: boolean; mixed?: boolean }) {
  const { token } = theme.useToken()
  const filled = checked || mixed
  return (
    <span
      aria-hidden
      style={{
        width: 16,
        height: 16,
        flex: 'none',
        display: 'inline-grid',
        placeItems: 'center',
        borderRadius: token.borderRadiusSM,
        border: `1px solid ${filled ? token.colorPrimary : token.colorBorder}`,
        background: filled ? token.colorPrimary : token.colorBgContainer,
        color: token.colorTextLightSolid,
      }}
    >
      {checked ? <CheckOutlined style={{ fontSize: 10 }} /> : mixed && <MinusOutlined style={{ fontSize: 10 }} />}
    </span>
  )
}

/**
 * One filter's configuration. The row component (next step) takes an ordered array of these;
 * the trigger renders exactly one.
 */
export interface FilterConfig {
  /** Stable id - becomes the URL param name once the row is wired. */
  id: string
  /** Already-translated display label; doubles as the trigger's resting text. */
  label: string
  options: FilterOption[]
  /** false renders a single-select panel: picking a value replaces the selection and closes.
   * @default true */
  multiple?: boolean
  /** Keep the caller's order instead of the default A-Z. For lists whose order means something: a
   * status sequence, leave types in their admin-set position, a size scale.
   * @default false */
  keepOrder?: boolean
  /** Force the search box on or off. Unset, it appears once the list passes SEARCH_THRESHOLD. */
  searchable?: boolean
}

const CHEVRON_SIZE = 20

/** Lists longer than this get a search box - SelectField's documented cut-off, so the two families agree. */
export const SEARCH_THRESHOLD = 8

export function isSearchable(config: FilterConfig): boolean {
  return config.searchable ?? config.options.length > SEARCH_THRESHOLD
}

const collator = new Intl.Collator(undefined, { sensitivity: 'base', numeric: true })

export interface OptionNode {
  option: FilterOption
  children: OptionNode[]
}

/** The options as a tree, read from indentLevel. */
export function optionTree(options: FilterOption[]): OptionNode[] {
  const roots: OptionNode[] = []
  const stack: OptionNode[] = []
  for (const option of options) {
    const node: OptionNode = { option, children: [] }
    while (stack.length > (option.indentLevel ?? 0)) stack.pop()
    ;(stack.length === 0 ? roots : stack[stack.length - 1].children).push(node)
    stack.push(node)
  }
  return roots
}

/**
 * A-Z by label. A hierarchical list (indentLevel) sorts siblings under their own parent and keeps
 * children beneath it, so the tree survives the sort.
 */
export function orderOptions(options: FilterOption[]): FilterOption[] {
  if (options.every((option) => !option.indentLevel)) {
    return [...options].sort((a, b) => collator.compare(a.label, b.label))
  }
  const flatten = (nodes: OptionNode[]): FilterOption[] =>
    [...nodes].sort((a, b) => collator.compare(a.option.label, b.option.label)).flatMap((node) => [node.option, ...flatten(node.children)])
  return flatten(optionTree(options))
}

/** What a row's box ticks: its own value, unless it is a group heading, and every value nested under it. */
export function valuesUnder(node: OptionNode): string[] {
  return [...(node.option.group ? [] : [node.option.value]), ...node.children.flatMap(valuesUnder)]
}

export interface VisibleRow {
  node: OptionNode
  depth: number
  /** A single-select list's group heading, drawn above the first row under it. */
  heading?: FilterOption
}

export interface VisibleRowsInput {
  nodes: OptionNode[]
  query: string
  /** Values of the groups closed by hand. A search opens every group it shows. */
  closed: Set<string>
  depth?: number
}

/** The rows a panel shows: every match with the groups it sits in. A group whose own name matches keeps all its rows. */
export function visibleRows(input: VisibleRowsInput): VisibleRow[] {
  const { nodes, query, closed, depth = 0 } = input
  const searching = query.trim() !== ''
  return nodes.flatMap((node) => {
    if (!searching || matchesQuery(node.option.label, query)) {
      const open = node.children.length > 0 && (searching || !closed.has(node.option.value))
      const inside = open ? visibleRows({ nodes: node.children, query: '', closed: searching ? new Set() : closed, depth: depth + 1 }) : []
      return [{ node, depth }, ...inside]
    }
    const inside = visibleRows({ nodes: node.children, query, closed, depth: depth + 1 })
    return inside.length > 0 ? [{ node, depth }, ...inside] : []
  })
}

/**
 * A single-select list's rows, flat and indented. A group heading rides on the first row under it rather than being a
 * row of its own, so it can never be picked or take the arrow keys. A heading whose name matches keeps all its rows.
 */
export function singleSelectRows(options: FilterOption[], query: string): VisibleRow[] {
  const rows: VisibleRow[] = []
  let heading: FilterOption | undefined
  let headingShown = false
  for (const option of options) {
    if (option.group) {
      heading = option
      headingShown = false
      continue
    }
    if (heading && (option.indentLevel ?? 0) <= (heading.indentLevel ?? 0)) {
      heading = undefined
    }
    if (!matchesQuery(option.label, query) && !(heading && matchesQuery(heading.label, query))) {
      continue
    }
    rows.push({ node: { option, children: [] }, depth: option.indentLevel ?? 0, ...(heading && !headingShown ? { heading } : {}) })
    headingShown = Boolean(heading)
  }
  return rows
}

/** The options in the order the panel shows them. */
export function panelOptions(config: FilterConfig): FilterOption[] {
  return config.keepOrder ? config.options : orderOptions(config.options)
}

const fold = (text: string) =>
  text
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()

/** Contains, ignoring case and accents: "sao" finds "São Paulo". */
export function matchesQuery(label: string, query: string): boolean {
  return fold(label).includes(fold(query.trim()))
}

export interface FilterTriggerProps {
  config: FilterConfig
  value: string[]
  onChange: (value: string[]) => void
  /** Accessible name for the clear ×. English default - pass a translation at the call site
   * (the DataTable convention: this component takes no i18n dependency itself). */
  clearLabel?: string
  /** Where the options panel portals to. The More overflow popover sets this to its own body
   * so a click inside a nested panel doesn't read as "outside" and close the overflow. */
  popupContainer?: (trigger: HTMLElement) => HTMLElement
  /** A permission scope note: renders a lock in the trigger with this text as its tooltip. The
   * trigger stays fully interactive - its option list is already narrowed. */
  lockReason?: string
  /** Renders the trigger muted and inert (e.g. a plan-gated filter): visible for discoverability,
   * not openable. Pair with lockReason to say why. */
  disabled?: boolean
  /** Render as if empty - resting label, chevron, no × - even while a value is set. For
   * filters whose current value IS their default (e.g. Status: Active) rather than a
   * narrowing; the panel still shows the true selection. */
  quiet?: boolean
  /** false hides the clear × while keeping the active styling and the chevron. For filters
   * that must always have a value (a single-select with a default), where "clearing" is
   * meaningless and the × would only mislead.
   * @default true */
  clearable?: boolean
  /** Rendered below the option list, separated by a rule - a summary line, an Apply/Cancel pair.
   * Its presence changes single-select semantics: picking an option STAGES it instead of
   * closing the panel, because the footer owns confirmation. */
  footer?: React.ReactNode
  /** Controlled open state, for callers whose footer closes the panel (Apply/Cancel) or holds
   * it open while submitting. Omit both to keep the internal behavior. */
  open?: boolean
  onOpenChange?: (open: boolean) => void
  /** Which side the panel opens on. A trigger docked to the bottom of the viewport (a bulk
   * actions bar) must open upward.
   * @default 'bottomLeft' */
  placement?: 'bottomLeft' | 'topLeft'
  /** Rendered inside the panel when `config.options` is empty, so the dropdown reads as an
   * intentional empty state instead of a tiny blank box. English default - pass a translation
   * at the call site.
   * @default 'No data' */
  emptyLabel?: string
  /** Richer empty-state override - a ReactNode that replaces the plain `emptyLabel` text when
   * `config.options` is empty (e.g. a "No labels created yet" link to a KB article). Ignored
   * when the trigger is disabled - the panel isn't openable then. */
  emptyContent?: React.ReactNode
  /** Placeholder and accessible name of the search box. English default - pass a translation.
   * @default 'Search' */
  searchLabel?: string
  /** Shown when the search matches nothing; `{query}` is replaced with what was typed. English
   * default - pass a translation.
   * @default 'No results for “{query}”' */
  noResultsLabel?: string
  /** Accessible name of a group's arrow while the group is closed; `{group}` is replaced with its
   * name. English default - pass a translation.
   * @default 'Show {group}' */
  expandGroupLabel?: string
  /** Accessible name of a group's arrow while the group is open; `{group}` is replaced with its
   * name. English default - pass a translation.
   * @default 'Hide {group}' */
  collapseGroupLabel?: string
}

/**
 * Adjacent selected rows read as one block, so only the outer corners of a run of selected
 * options keep a radius - otherwise the tints meet in a pinched double-curve.
 */
export function selectedRunRadius(radius: number, prevSelected: boolean, nextSelected: boolean): string {
  const top = prevSelected ? 0 : radius
  const bottom = nextSelected ? 0 : radius
  return `${top}px ${top}px ${bottom}px ${bottom}px`
}

/**
 * The filter trigger: a compact control whose label collapses into its value once something is selected -
 * "Locations" → "Locations: Amsterdam +1" - with a tinted background and accent border as the
 * one active signal, and an × that clears just this filter.
 *
 * Anatomy note for review: the trigger is TWO real buttons in one visual shape, because an × inside
 * the opener would be a button nested in a button - invalid and unreachable by keyboard. The
 * opener carries aria-haspopup/aria-expanded and opens the options panel; the × is its sibling
 * with its own accessible name. Inactive triggers show a decorative chevron inside the opener
 * instead.
 *
 * Ant's Select cannot render this trigger natively (the reason this component exists), so the
 * panel is a Popover holding a listbox of real option buttons: neutral panel background, only
 * the selected rows tinted, selections applying immediately - multi-select keeps the panel open,
 * single-select closes on pick.
 */
export function FilterTrigger(props: FilterTriggerProps) {
  const {
    config,
    value,
    onChange,
    clearLabel,
    popupContainer,
    lockReason,
    disabled,
    quiet,
    clearable,
    footer,
    onOpenChange,
    placement = 'bottomLeft',
    emptyLabel = 'No data',
    emptyContent,
    searchLabel = 'Search',
    noResultsLabel = 'No results for “{query}”',
    expandGroupLabel = 'Show {group}',
    collapseGroupLabel = 'Hide {group}',
  } = props
  const { token } = theme.useToken()
  const [internalOpen, setInternalOpen] = useState(false)
  const open = props.open ?? internalOpen
  const setOpen = (next: boolean) => {
    if (props.open === undefined) {
      setInternalOpen(next)
    }
    onOpenChange?.(next)
  }
  // Roving tabindex: the whole list is one tab stop and the arrows move within it. Every option
  // being separately tabbable is what made a long filter a wall of tab stops.
  const [activeIndex, setActiveIndex] = useState(0)
  const optionRefs = useRef<(HTMLButtonElement | null)[]>([])
  const openerRef = useRef<HTMLButtonElement | null>(null)
  const inputRef = useRef<InputRef | null>(null)
  const multiple = config.multiple !== false
  const active = value.length > 0 && !quiet
  const showClear = active && clearable !== false
  const searchable = isSearchable(config)
  const [query, setQuery] = useState('')
  const ordered = useMemo(() => panelOptions(config), [config.options, config.keepOrder])
  const tree = useMemo(() => optionTree(ordered), [ordered])
  // Groups open and close in a multi-select list; a single-select one keeps plain indented rows.
  const grouped = multiple && tree.some((node) => node.children.length > 0)
  const [closed, setClosed] = useState<Set<string>>(() => new Set())
  // What the arrows move through, in the order shown.
  const rows = useMemo(() => (grouped ? visibleRows({ nodes: tree, query, closed }) : singleSelectRows(ordered, query)), [grouped, tree, ordered, query, closed])

  useEffect(() => {
    if (!open) {
      setQuery('')
      return
    }
    // Open on the first selected option, or the first option - never on wherever the last visit left it.
    const firstSelected = rows.findIndex(({ node }) => value.includes(node.option.value))
    setActiveIndex(firstSelected === -1 ? 0 : firstSelected)
    // Not on rows: opening or closing a group must leave focus on that group.
  }, [open, value])

  // With a search box the box takes focus; the list is reached by typing or Down.
  useEffect(() => {
    if (open && searchable) inputRef.current?.focus()
  }, [open, searchable])

  useEffect(() => {
    if (open && !searchable) optionRefs.current[activeIndex]?.focus()
  }, [open, activeIndex, searchable])

  const labelByValue = useMemo(() => new Map(config.options.map((option) => [option.value, option.label])), [config.options])

  const triggerText = active ? `${config.label}: ${labelByValue.get(value[0]) ?? value[0]}${value.length > 1 ? ` +${value.length - 1}` : ''}` : config.label

  const toggleOption = (optionValue: string) => {
    if (!multiple) {
      onChange(value[0] === optionValue ? [] : [optionValue])
      if (!footer) {
        setOpen(false)
      }
      return
    }
    onChange(value.includes(optionValue) ? value.filter((entry) => entry !== optionValue) : [...value, optionValue])
  }

  const toggleGroup = (node: OptionNode) => {
    const values = valuesUnder(node)
    const all = values.every((entry) => value.includes(entry))
    onChange(all ? value.filter((entry) => !values.includes(entry)) : [...value, ...values.filter((entry) => !value.includes(entry))])
  }

  const setGroupOpen = (groupValue: string, next: boolean) => {
    setClosed((previous) => {
      const updated = new Set(previous)
      if (next) updated.delete(groupValue)
      else updated.add(groupValue)
      return updated
    })
  }

  const trigger: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'stretch',
    borderRadius: token.borderRadius,
    border: `1px solid ${active ? token.colorPrimaryBorder : token.colorBorder}`,
    background: active ? token.colorPrimaryBg : token.colorBgContainer,
    color: disabled ? token.colorTextDisabled : active ? token.colorPrimary : token.colorText,
    fontSize: token.fontSize,
    lineHeight: `${token.controlHeight}px`,
    opacity: disabled ? 0.65 : undefined,
    transition: 'background-color 120ms ease-out, border-color 120ms ease-out',
  }

  const buttonReset: React.CSSProperties = {
    background: 'none',
    border: 'none',
    padding: 0,
    font: 'inherit',
    color: 'inherit',
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    gap: token.paddingXXS,
  }

  const focusOption = (index: number) => {
    setActiveIndex(index)
    optionRefs.current[index]?.focus()
  }

  const move = (delta: number) => {
    const count = rows.length
    if (count === 0) return
    // Up from the first row returns to the search box rather than wrapping to the bottom.
    if (searchable && delta < 0 && activeIndex === 0) {
      inputRef.current?.focus()
      return
    }
    focusOption((activeIndex + delta + count) % count)
  }

  const close = () => {
    setOpen(false)
    // Focus goes back to what opened the panel, not to the top of the document.
    openerRef.current?.focus()
  }

  const onPanelKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault()
        move(1)
        break
      case 'ArrowUp':
        event.preventDefault()
        move(-1)
        break
      case 'Home':
        event.preventDefault()
        focusOption(0)
        break
      case 'End':
        event.preventDefault()
        focusOption(Math.max(0, rows.length - 1))
        break
      case 'ArrowRight':
      case 'ArrowLeft': {
        const node = rows[activeIndex]?.node
        if (grouped && node && node.children.length > 0 && !query.trim()) {
          event.preventDefault()
          setGroupOpen(node.option.value, event.key === 'ArrowRight')
        }
        break
      }
      case 'Escape':
        event.preventDefault()
        close()
        break
      default:
        break
    }
  }

  const onSearchKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault()
        if (rows.length > 0) focusOption(0)
        break
      case 'Escape':
        // First Escape clears what was typed; the second closes, like a combobox.
        event.preventDefault()
        if (query) setQuery('')
        else close()
        break
      default:
        break
    }
  }

  const search = searchable && (
    <div style={{ borderBottom: `1px solid ${token.colorSplit}`, marginBottom: token.paddingXXS }}>
      <Input
        ref={inputRef}
        variant="borderless"
        allowClear
        prefix={<SearchOutlined aria-hidden style={{ color: token.colorTextTertiary }} />}
        placeholder={searchLabel}
        aria-label={searchLabel}
        value={query}
        onChange={(event) => {
          setQuery(event.target.value)
          setActiveIndex(0)
        }}
        onKeyDown={onSearchKeyDown}
      />
    </div>
  )

  /** Where a grouped row's content starts: the arrow's width plus its gap for every level, so a row lines up under its group's name. */
  const nest = CHEVRON_SIZE + token.paddingXS

  function optionRow({ node, depth }: VisibleRow, index: number) {
    const { option, children } = node
    const isGroup = grouped && children.length > 0
    const values = isGroup ? valuesUnder(node) : [option.value]
    const ticked = values.filter((entry) => value.includes(entry)).length
    const selected = ticked > 0 && ticked === values.length
    const prevSelected = index > 0 && value.includes(rows[index - 1].node.option.value)
    const nextSelected = index < rows.length - 1 && value.includes(rows[index + 1].node.option.value)
    const groupOpen = isGroup && (query.trim() !== '' || !closed.has(option.value))
    const paddingLeft = !grouped ? token.paddingSM + depth * token.padding : isGroup ? token.paddingXS : token.paddingXS + Math.max(depth, 1) * nest
    const button = (
      <button
        ref={(element) => {
          optionRefs.current[index] = element
        }}
        type="button"
        role="option"
        aria-selected={selected}
        tabIndex={index === activeIndex ? 0 : -1}
        className={multiple ? 'vt-filter-trigger-option' : 'vt-filter-trigger-option vt-filter-trigger-option--tinted'}
        onClick={() => (isGroup ? toggleGroup(node) : toggleOption(option.value))}
        style={{
          ...buttonReset,
          flex: 1,
          minWidth: 0,
          justifyContent: 'space-between',
          padding: `${token.paddingXS}px ${token.paddingSM}px`,
          paddingLeft,
          gap: token.paddingSM,
          borderRadius: selected && !multiple ? selectedRunRadius(token.borderRadius, prevSelected, nextSelected) : `${token.borderRadius}px`,
          // Single-select tints its one pick; multi-select ends each row in a box instead.
          background: selected && !multiple ? token.colorPrimaryBg : 'transparent',
          color: selected && !multiple ? token.colorPrimary : token.colorText,
        }}
      >
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: token.paddingXS }}>
          {option.swatch && <OptionSwatch color={option.swatch} />}
          {option.label}
          {isGroup && (
            <span style={{ padding: `0 ${token.paddingXXS + 2}px`, borderRadius: token.borderRadiusSM, background: token.colorFillTertiary, color: token.colorTextSecondary }}>
              {children.length}
            </span>
          )}
        </span>
        {multiple ? <OptionBox checked={selected} mixed={ticked > 0 && !selected} /> : selected && <CheckOutlined aria-hidden style={{ fontSize: 12 }} />}
      </button>
    )
    if (!isGroup) {
      return (
        <div key={option.value} style={{ display: 'flex' }}>
          {button}
        </div>
      )
    }
    const arrowLabel = (groupOpen ? collapseGroupLabel : expandGroupLabel).replace('{group}', option.label)
    return (
      <div
        key={option.value}
        className="vt-filter-trigger-group"
        style={{ display: 'flex', alignItems: 'center', paddingLeft: token.paddingXS + depth * nest, borderRadius: token.borderRadius }}
      >
        {/* Out of the tab order: the arrows open and close the group from its row. */}
        <button
          type="button"
          tabIndex={-1}
          className="vt-filter-trigger-chevron"
          aria-label={arrowLabel}
          aria-expanded={groupOpen}
          disabled={query.trim() !== ''}
          onClick={() => setGroupOpen(option.value, !groupOpen)}
          style={{
            ...buttonReset,
            width: CHEVRON_SIZE,
            height: CHEVRON_SIZE,
            flex: 'none',
            justifyContent: 'center',
            borderRadius: token.borderRadiusSM,
            color: token.colorTextSecondary,
          }}
        >
          <DownOutlined aria-hidden style={{ fontSize: 10, transform: groupOpen ? undefined : 'rotate(-90deg)', transition: 'transform 120ms ease-out' }} />
        </button>
        {button}
      </div>
    )
  }

  /** A rule above each top-level group, and above the first plain row after one. */
  const ruleAbove = (index: number) => {
    if (!grouped || index === 0 || rows[index].depth > 0) return false
    const isGroup = rows[index].node.children.length > 0
    const previousTop = [...rows.slice(0, index)].reverse().find((row) => row.depth === 0)
    return isGroup || (previousTop?.node.children.length ?? 0) > 0
  }

  const panel = (
    <div
      role="listbox"
      onKeyDown={onPanelKeyDown}
      aria-label={config.label}
      aria-multiselectable={multiple || undefined}
      style={{ display: 'flex', flexDirection: 'column', minWidth: 200, maxHeight: '50vh', overflowY: 'auto' }}
    >
      {config.options.length === 0 && (
        <div role="status" style={{ padding: `${token.paddingSM}px`, color: token.colorTextSecondary, textAlign: 'center', fontSize: token.fontSize }}>
          {emptyContent ?? emptyLabel}
        </div>
      )}
      {config.options.length > 0 && rows.length === 0 && (
        <div role="status" style={{ padding: `${token.paddingSM}px`, color: token.colorTextSecondary, textAlign: 'center', fontSize: token.fontSize }}>
          {noResultsLabel.replace('{query}', query.trim())}
        </div>
      )}
      {rows.map((row, index) => (
        <React.Fragment key={row.node.option.value}>
          {ruleAbove(index) && <div role="presentation" style={{ height: 1, flex: 'none', background: token.colorSplit, margin: `${token.paddingXXS}px 0` }} />}
          {row.heading && (
            <div
              role="presentation"
              style={{ padding: `${token.paddingXS}px ${token.paddingSM}px ${token.paddingXXS}px`, color: token.colorTextSecondary, fontSize: token.fontSizeSM }}
            >
              {row.heading.label}
            </div>
          )}
          {optionRow(row, index)}
        </React.Fragment>
      ))}
    </div>
  )

  const content =
    footer || search ? (
      <div style={{ display: 'flex', flexDirection: 'column', minWidth: searchable ? 240 : undefined }}>
        {search}
        {panel}
        {footer && (
          <div
            style={{
              borderTop: `1px solid ${token.colorSplit}`,
              marginTop: token.paddingXXS,
              padding: `${token.paddingXS}px ${token.paddingSM}px ${token.paddingXS - token.paddingXXS}px`,
            }}
          >
            {footer}
          </div>
        )}
      </div>
    ) : (
      panel
    )

  return (
    <>
      {/* Scoped focus/hover styles: inline styles can't express :focus-visible. The opener's
          ring wraps the WHOLE chip (via :has on the wrapper) because the chip is the perceived
          control; ringing just the opener segment would exclude the × and cut through the shape.
          The × keeps its own compact inset ring so the two tab stops stay distinguishable. */}
      <style>{`
        .vt-filter-trigger:has(> .vt-filter-trigger-opener:focus-visible) {
          outline: 2px solid ${token.colorPrimary};
          outline-offset: 2px;
        }
        .vt-filter-trigger-opener:focus-visible { outline: none; }
        .vt-filter-trigger-clear:focus-visible {
          outline: 2px solid ${token.colorPrimary};
          outline-offset: -4px;
          border-radius: 6px;
        }
        .vt-filter-trigger-option:focus-visible {
          outline: 2px solid ${token.colorPrimary};
          outline-offset: -2px;
        }
        .vt-filter-trigger-option:hover { background: ${token.colorFillTertiary} !important; }
        /* A group row is the arrow plus the option: hovering either lights the whole row. */
        .vt-filter-trigger-group:hover { background: ${token.colorFillTertiary}; }
        .vt-filter-trigger-group .vt-filter-trigger-option:hover { background: transparent !important; }
        .vt-filter-trigger-chevron:hover:not(:disabled) { background: ${token.colorFillSecondary} !important; }
        /* A selected row composites the same translucent fill OVER its tint instead of replacing
           it: swapping to the neutral grey would punch a hole through a run of adjacent
           selections, and the run's squared inner corners would leave that hole looking like a
           misdrawn block. Not colorPrimaryBgHover - that is derived from colorPrimary, so it
           hue-shifts away from the brand's colorPrimaryBg and vanishes entirely in dark. */
        .vt-filter-trigger-option--tinted[aria-selected="true"]:hover {
          background: linear-gradient(${token.colorFillTertiary}, ${token.colorFillTertiary}), ${token.colorPrimaryBg} !important;
        }
      `}</style>
      <span className="vt-filter-trigger" style={trigger}>
        {/* When the trigger is disabled AND has a lockReason (e.g. plan-gated Labels on Core),
            wrap the whole opener in a Tooltip so the reason is discoverable by hovering anywhere
            on the button - not just the small lock icon inside. Non-disabled locked triggers
            (scope-locked but interactive) keep the lock icon with its own tooltip. */}
        {disabled && lockReason ? (
          <Tooltip title={lockReason}>
            <button
              ref={openerRef}
              type="button"
              className="vt-filter-trigger-opener"
              aria-haspopup="listbox"
              aria-expanded={false}
              disabled
              style={{
                ...buttonReset,
                cursor: 'not-allowed',
                padding: `0 ${token.padding}px 0 ${token.padding}px`,
              }}
            >
              {triggerText}
              <LockOutlined aria-hidden style={{ fontSize: 11 }} />
              <DownOutlined aria-hidden style={{ fontSize: 10 }} />
            </button>
          </Tooltip>
        ) : (
          <Popover
            open={open}
            onOpenChange={setOpen}
            trigger="click"
            placement={placement}
            arrow={false}
            content={content}
            getPopupContainer={popupContainer}
            styles={{ body: { padding: token.paddingXXS } }}
          >
            <button
              ref={openerRef}
              type="button"
              className="vt-filter-trigger-opener"
              aria-haspopup="listbox"
              aria-expanded={open}
              disabled={disabled}
              style={{
                ...buttonReset,
                cursor: disabled ? 'not-allowed' : buttonReset.cursor,
                padding: `0 ${showClear ? token.paddingXS : token.padding}px 0 ${token.padding}px`,
              }}
            >
              {triggerText}
              {lockReason && (
                <Tooltip title={lockReason}>
                  <LockOutlined aria-label={lockReason} style={{ fontSize: 11 }} />
                </Tooltip>
              )}
              {!showClear && <DownOutlined aria-hidden style={{ fontSize: 10 }} />}
            </button>
          </Popover>
        )}
        {showClear && (
          <button
            type="button"
            className="vt-filter-trigger-clear"
            aria-label={clearLabel ?? `Clear ${config.label}`}
            onClick={() => onChange([])}
            style={{ ...buttonReset, padding: `0 ${token.padding}px 0 ${token.paddingXXS}px` }}
          >
            <CloseOutlined aria-hidden style={{ fontSize: 11 }} />
          </button>
        )}
      </span>
    </>
  )
}
